import React, { useCallback, useEffect, useRef, useState } from 'react';

import { emitter, EventsEnum } from '@lyearn/events';
import { useTranslation } from '@lyearn/i18n';
import { getHTMLFromDoc, getMarkdownParser } from '@lyearn/lygo-core';
import { useGenerativeTaskSubscription } from '@/components/LyearnAI/graphql/subscriptions/generated/generativeTaskSubscription';
import { useUpdateChannelMutation } from '@/features/messages/graphql/mutations/generated/updateChannel';
import { extractPlaintextFromHTML } from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageListItem/utils';
import useRichEditorProps from '@/modules/corporate/modules/course/hooks/useRichEditorProps';
import { useCreateNoteMutation } from '@/modules/knowledge-base-copilot/graphql/mutations/generated/createNote';
import { AIResponse } from '@/modules/knowledge-base-copilot/types';
import { GenerativeTaskStatusEnum, NoteTypeEnum } from '@/types/schema';

// The coach answer converter pulls in markdown-it and its plugins (markdown-it,
// markdown-it-container, @vscode/markdown-it-katex). Those are only needed once a
// coach answer actually streams, so we code-split them into their own async chunk
// and load on demand instead of shipping them in the initial bundle — this hook
// lives in the broadly-mounted HelpMenu, so a static import would weigh down the
// homepage. The promise is module-level so the chunk is fetched at most once.
type CoachAnswerConverter = (markdown: string) => string;
let coachConverterPromise: Promise<CoachAnswerConverter> | null = null;
const loadCoachConverter = (): Promise<CoachAnswerConverter> => {
  if (!coachConverterPromise) {
    coachConverterPromise = import('@/modules/knowledge-base-copilot/coachAnswerMarkdown')
      .then((mod) => mod.convertCoachAnswerToHtml)
      .catch((e) => {
        // Clear the cache so a later attempt can retry if the chunk failed to load
        // (e.g. a transient network error or a stale chunk after a deploy).
        coachConverterPromise = null;
        throw e;
      });
  }
  return coachConverterPromise;
};

// The teach-mode "document" is still parsed with the editor markdown parser, so
// it keeps the legacy clean-up that parser needs (it throws on a bare `---` /hr
// and treats trailing double-space line breaks specially). The chat *answer* is
// no longer run through this — it goes through the markdown-it converter, which
// handles `---` natively and where stripping it would destroy GitHub table
// separator rows (`| --- | --- |`).
const sanitizeString = (str?: string) =>
  typeof str === 'string' ? str.replace(/---/g, '').replace(/ {2,}\n/g, '\n') : str;

const useGetAIResponse = ({
  generativeTaskId_,
  channelId,
  setIsWaiting,
  channelTitle,
  setChannelTitle,
  followUpQuestions,
  setFollowUpQuestions,
  isFreshChat,
  setIsFreshChat,
  setError,
}: {
  generativeTaskId_?: string | null;
  channelId?: string | null;
  setIsWaiting: (isWaiting: boolean) => void;
  channelTitle: string;
  setChannelTitle: (newChannelTitle: string) => void;
  followUpQuestions: Record<string, string[]>;
  setFollowUpQuestions: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  isFreshChat: boolean;
  setIsFreshChat: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [aiResponse, setAIResponse] = useState<AIResponse | null>(null);
  const [createdPostTaskId, setCreatedPostTaskId] = useState<string | undefined>('');
  const [, createNote] = useCreateNoteMutation();
  const [, updateChannelTitle] = useUpdateChannelMutation();
  const { t } = useTranslation(['AI', 'Common']);

  const [isSubscribing, setIsSubscribing] = useState(false);

  // Lazily-loaded coach markdown converter (see loadCoachConverter above). Held in
  // a ref so the streaming effect can call it synchronously; the boolean flips once
  // the chunk resolves, re-running that effect so the first chunk renders promptly.
  const coachConverterRef = useRef<CoachAnswerConverter | null>(null);
  const [isCoachConverterReady, setIsCoachConverterReady] = useState(false);

  // Warm the converter chunk only once a coach task is actually in flight, so pages
  // that merely mount the HelpMenu (e.g. the homepage) never pay to download it. The
  // import resolves well before the first streamed chunk; if not, the ready flag
  // re-runs the render effect below.
  useEffect(() => {
    if (!generativeTaskId_) {
      return undefined;
    }
    let active = true;
    loadCoachConverter()
      .then((convert) => {
        if (active) {
          coachConverterRef.current = convert;
          setIsCoachConverterReady(true);
        }
      })
      .catch((e) => {
        if (active) {
          console.error(e);
        }
      });
    return () => {
      active = false;
    };
  }, [generativeTaskId_]);

  const editorProps = useRichEditorProps({
    t,
  });

  // if generativeTaskSubscriptionFetching goes from false to true, we can stop 'waiting'
  const [{ data: generativeTaskSubscriptionData, fetching: generativeTaskSubscriptionFetching }] =
    useGenerativeTaskSubscription({
      variables: {
        data: {
          // you get a new generativeTaskId_ from the parent component and fetch the result every time it changes
          // Until the value of generativeTaskId_ changes, the data is cached
          generativeTaskId: generativeTaskId_ || '',
        },
      },
      pause: !generativeTaskId_?.length, // doesn't run for empty string
    });

  const teachEnabledResult =
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.result.trainTheKnowledgeBase;

  const teachDisabledResult =
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.result.chatWithKnowledgeBase;

  // The chat answer is rendered with the markdown-it converter, so it is passed
  // through verbatim (stripping `---` here would break GitHub table separators).
  const result = teachEnabledResult?.answer || teachDisabledResult?.answer;
  // The teach-mode document still goes through the editor parser, which needs the
  // legacy clean-up.
  const resultDoc = sanitizeString(teachEnabledResult?.document || undefined);
  const docTitle = teachEnabledResult?.title || null;
  const docDescription = teachEnabledResult?.description || null;

  /**
   * result will contain:
   * - chatWithKnowledgeBase: usual answers (not in coach the coach mode)
   * - trainTheKnowledgeBase: coach mode answers
   * - - answer
   * - - document (md type) [stream doc response -> call create note -> get entity id -> createPost -> mentions will be entity id in the createPost payload]
   */

  // status can be cancelled, completed, failed, processing, or toBeProcessed
  const status = generativeTaskSubscriptionData?.generativeTask.generativeTask?.status;
  const currentTaskId = generativeTaskSubscriptionData?.generativeTask.generativeTaskId;

  // contains follow up questions (shown a few secondas after the response is generated completely)
  const updatedFollowUpQuestions =
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.result.chatWithKnowledgeBase
      ?.followUpQuestions;

  const subscribedGenerativeTaskId =
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.id;

  // Persist the latest follow-up questions once the answer completes. Runs in an
  // effect (not during render) and compares by content so we don't loop on the
  // array identity changing every subscription tick.
  useEffect(() => {
    if (
      channelId &&
      updatedFollowUpQuestions?.length &&
      status === GenerativeTaskStatusEnum.Completed &&
      generativeTaskId_ === subscribedGenerativeTaskId
    ) {
      setFollowUpQuestions((prev) => {
        const existing = prev[channelId];
        if (existing && existing.join('\u0000') === updatedFollowUpQuestions.join('\u0000')) {
          return prev;
        }
        return { ...prev, [channelId]: updatedFollowUpQuestions };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, updatedFollowUpQuestions, status, generativeTaskId_, subscribedGenerativeTaskId]);

  const suggestedChatTitle =
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.result.chatWithKnowledgeBase
      ?.title ||
    generativeTaskSubscriptionData?.generativeTask.generativeTask?.result.trainTheKnowledgeBase
      ?.title;

  // Update the channel title for a fresh chat. Also moved into an effect to avoid
  // calling setState/mutations during render.
  useEffect(() => {
    if (
      channelId &&
      isFreshChat &&
      suggestedChatTitle &&
      suggestedChatTitle !== channelTitle &&
      generativeTaskId_ === subscribedGenerativeTaskId
    ) {
      setChannelTitle(suggestedChatTitle);
      setIsFreshChat(false);
      updateChannelTitle({
        data: {
          channelId,
          name: suggestedChatTitle,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channelId,
    isFreshChat,
    suggestedChatTitle,
    channelTitle,
    generativeTaskId_,
    subscribedGenerativeTaskId,
  ]);

  const handleDocCreation = useCallback(
    async (
      htmlContent: string | null,
      documentTitle: string | null,
      documentDescription: string | null,
    ) => {
      if (htmlContent) {
        try {
          const title = documentTitle || channelTitle || t('Common:KnowledgeBase.UntitledNote');
          const plainText = extractPlaintextFromHTML(htmlContent);
          await createNote({
            input: {
              title,
              htmlContent,
              plainText,
              description: documentDescription,
              type: NoteTypeEnum.KnowledgeDoc,
            },
          });

          emitter.emit(EventsEnum.KnowledgeDocCreation);
        } catch (error) {
          // show snackbar here later
          console.error(error);
        }
      }
    },
    [createNote, channelTitle, t],
  );

  useEffect(() => {
    if (generativeTaskSubscriptionFetching && !isSubscribing) {
      setIsSubscribing(true);
    }
    if (result || resultDoc) {
      // Answer: render with the same markdown-it pipeline the backend uses so
      // tables/callouts/tips/math/code render progressively as they stream and
      // the live answer is identical to the persisted post (no flash on
      // completion). markdown-it tolerates partial/streaming markdown; the
      // try/catch is purely defensive so a pathological chunk can never break
      // the subscription effect.
      let html: string | null = null;
      const convertCoachAnswerToHtml = coachConverterRef.current;
      if (result && convertCoachAnswerToHtml) {
        try {
          html = convertCoachAnswerToHtml(result);
        } catch (e) {
          console.error(e);
        }
      }

      // Teach-mode document: still parsed with the editor markdown parser (its
      // HTML feeds createNote and carries entity mentions for the doc flow).
      let doc: string | null = null;
      if (resultDoc) {
        try {
          const { schema, markdownTokens } = editorProps;
          const markdownParser = getMarkdownParser(schema, markdownTokens);
          const mdDoc = markdownParser.parse(resultDoc);
          doc = mdDoc ? getHTMLFromDoc(mdDoc, schema) : null;
        } catch (e) {
          console.error(e);
        }
      }

      if ((html || doc) && channelId) {
        if (GenerativeTaskStatusEnum.Completed == status && createdPostTaskId != currentTaskId) {
          // After the AI response is generated completely, we create docs associated with the post
          // we check createdPostTaskId != currentTaskId only to avoid creating multiple posts during rerenders
          setCreatedPostTaskId(currentTaskId);
          handleDocCreation(doc, docTitle, docDescription);
          setIsSubscribing(false);
          setAIResponse(null);
        } else if (status != GenerativeTaskStatusEnum.Completed) {
          // can add better handling for other statuses here
          setAIResponse({
            answer: html || '',
            doc: doc || '',
          });
          setIsWaiting(false);
        }
      }
    }
  }, [
    result,
    resultDoc,
    docTitle,
    docDescription,
    status,
    currentTaskId,
    createdPostTaskId,
    editorProps,
    channelId,
    generativeTaskSubscriptionFetching,
    isCoachConverterReady,
  ]);

  return {
    aiResponse,
    isSubscribing,
    setAIResponse,
  };
};

export default useGetAIResponse;
